import { createContext, useContext, useState, useCallback, useEffect } from 'react';

/**
 * Lightweight toast system. ToastProvider holds the queue, useToast()
 * gives any component:
 *
 *   const { toast } = useToast();
 *   toast.error('Stripe not configured', 'Add credentials in Admin → Payment Settings');
 *   toast.info('Saved');
 *   toast.success('Profile updated');
 *
 * Each toast auto-dismisses after 5s. Built rather than importing a library
 * because the rest of the UI is hand-rolled vanilla CSS in this codebase.
 */

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const push = useCallback((kind, title, detail) => {
    const id = nextId++;
    setToasts(prev => [...prev, { id, kind, title, detail }]);
    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const api = {
    error:   (title, detail) => push('error',   title, detail),
    success: (title, detail) => push('success', title, detail),
    info:    (title, detail) => push('info',    title, detail),
  };

  return (
    <ToastContext.Provider value={{ toast: api, dismiss }}>
      {children}
      <ToastViewport toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Render-time fallback: in tests or accidental renders without the
    // provider, we want a callable shape instead of a crash.
    return { toast: { error: () => {}, success: () => {}, info: () => {} }, dismiss: () => {} };
  }
  return ctx;
}

const KIND_STYLES = {
  error:   { accent: '#DC2626', bg: '#FEF2F2', border: '#FCA5A5', icon: '⚠' },
  success: { accent: '#0F9D6E', bg: '#ECFDF5', border: '#86EFAC', icon: '✓' },
  info:    { accent: '#6B2FD9', bg: '#F3EEFF', border: '#C4B5FD', icon: 'ⓘ' },
};

function ToastViewport({ toasts, dismiss }) {
  if (toasts.length === 0) return null;
  return (
    <div
      role="region"
      aria-label="Notifications"
      style={{
        position: 'fixed', top: 90, right: 20, zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 380,
        pointerEvents: 'none',
      }}
    >
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }) {
  const style = KIND_STYLES[toast.kind] || KIND_STYLES.info;
  useEffect(() => {
    const id = setTimeout(onDismiss, 5000);
    return () => clearTimeout(id);
  }, [onDismiss]);

  return (
    <div
      role="status"
      style={{
        pointerEvents: 'auto',
        background: '#fff',
        border: `1px solid ${style.border}`,
        borderLeft: `4px solid ${style.accent}`,
        borderRadius: 12,
        padding: '14px 16px 14px 14px',
        boxShadow: '0 12px 32px rgba(15,18,37,0.12)',
        display: 'flex', alignItems: 'flex-start', gap: 10,
        animation: 'toast-in 0.18s ease',
      }}
    >
      <span style={{ fontSize: 16, color: style.accent, lineHeight: 1.4, flexShrink: 0 }} aria-hidden="true">{style.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 14, fontWeight: 700, color: '#0F1225', lineHeight: 1.4, margin: 0 }}>
          {toast.title}
        </p>
        {toast.detail && (
          <p style={{ fontSize: 13, color: '#4A5568', lineHeight: 1.5, margin: '4px 0 0' }}>
            {toast.detail}
          </p>
        )}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss notification"
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: '#8B94A8', fontSize: 18, lineHeight: 1, padding: 2,
          fontFamily: 'inherit',
        }}
      >×</button>
      <style>{`
        @keyframes toast-in {
          from { transform: translateX(20px); opacity: 0; }
          to   { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
